import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { SessionProvider, useSessionLoader } from './api/session';
import { Shell } from './app/Shell';
import { UIProvider } from './app/ui';
import { ErrorState, Skeleton } from './components/ui';
import { InstallPrompt, UpdatePrompt } from './pwa';
import { Dashboard } from './pages/Dashboard';
import { LoginPage } from './pages/Login';
import { TaskListPage } from './pages/TaskList';

// Less-frequently used screens are code-split.
const TaskDetailPage = lazy(() => import('./pages/TaskDetail'));
const PresetsPage = lazy(() => import('./pages/Presets'));
const TeamPage = lazy(() => import('./pages/Team'));
const HistoryPage = lazy(() => import('./pages/History'));
const ApprovalsPage = lazy(() => import('./pages/Approvals'));
const ApprovalDetailPage = lazy(() => import('./pages/ApprovalDetail'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const SavedViewPage = lazy(() => import('./pages/SavedView'));

function Protected() {
  const me = useSessionLoader();
  const loc = useLocation();
  if (me.isPending)
    return (
      <div className="content">
        <Skeleton rows={6} />
      </div>
    );
  if (me.isError) {
    const status = (me.error as { status?: number }).status;
    if (status === 401) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
    return (
      <div className="content">
        <ErrorState title="Task OS could not start" error={me.error} onRetry={() => me.refetch()} />
      </div>
    );
  }
  return (
    <SessionProvider me={me.data}>
      <UIProvider>
        <Shell>
          <Suspense fallback={<Skeleton rows={5} />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/inbox" element={<TaskListPage view="inbox" />} />
              <Route path="/today" element={<TaskListPage view="today" />} />
              <Route path="/overdue" element={<TaskListPage view="overdue" />} />
              <Route path="/followups" element={<TaskListPage view="followups" />} />
              <Route path="/upcoming" element={<TaskListPage view="upcoming" />} />
              <Route path="/waiting" element={<TaskListPage view="waiting" />} />
              <Route path="/recurring" element={<TaskListPage view="recurring" />} />
              <Route path="/mine" element={<TaskListPage view="mine" />} />
              <Route path="/tasks" element={<TaskListPage view="all" />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/tasks/:id" element={<TaskDetailPage />} />
              <Route path="/presets" element={<PresetsPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/approvals" element={<ApprovalsPage />} />
              <Route path="/approvals/:id" element={<ApprovalDetailPage />} />
              <Route path="/views/:id" element={<SavedViewPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/:section" element={<SettingsPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Shell>
        <UpdatePrompt />
        <InstallPrompt />
      </UIProvider>
    </SessionProvider>
  );
}

function NotFound() {
  return (
    <div className="empty">
      <div className="big">🧭</div>
      <strong>Page not found</strong>
      <a href="/">Go to the dashboard</a>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="*" element={<Protected />} />
    </Routes>
  );
}
